import { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  TrendingUp,
  TrendingDown,
  Eye,
  EyeOff,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { useQuadrantStore } from "../store/quadrantStore";
import {
  getZoneColor,
  getZoneLabelKey,
  type SignalZone,
} from "../types/quadrant";
import { cn } from "../utils/cn";

// Get icon component by zone name
function getZoneIcon(zone: SignalZone) {
  if (zone.includes("LONG")) {
    return <TrendingUp className="w-4 h-4" />;
  }
  return <TrendingDown className="w-4 h-4" />;
}

interface ZoneStat {
  zone: SignalZone;
  count: number;
  icon: React.ReactNode;
}

export function QuadrantLegend() {
  const { t } = useTranslation();
  // Subscribe to points directly to trigger re-renders
  const points = useQuadrantStore((state) => state.points);
  const filters = useQuadrantStore((state) => state.filters);
  const setFilter = useQuadrantStore((state) => state.setFilter);
  const refreshData = useQuadrantStore((state) => state.refreshData);
  const loading = useQuadrantStore((state) => state.loading);
  const lastUpdate = useQuadrantStore((state) => state.lastUpdate);

  // Search dropdown state
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(filters.symbolFilter);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync external filter changes
  useEffect(() => {
    setInputValue(filters.symbolFilter);
  }, [filters.symbolFilter]);

  // Update dropdown position when shown
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  useEffect(() => {
    if (showDropdown) {
      updateDropdownPosition();
    }
  }, [showDropdown]);

  // Get matching symbols for dropdown
  const matchingSymbols = useMemo(() => {
    if (!inputValue.trim()) return [];
    const query = inputValue.toLowerCase().trim();
    const uniqueSymbols = [...new Set(points.map((p) => p.symbol))];
    return uniqueSymbols
      .filter((s) => s.toLowerCase().includes(query))
      .slice(0, 10);
  }, [points, inputValue]);

  // Memoize filtered points
  const filteredPoints = useMemo(() => {
    let filtered = points;
    if (filters.minScore > 0) {
      filtered = filtered.filter((p) => Math.abs(p.score) >= filters.minScore);
    }
    if (!filters.showNeutral) {
      filtered = filtered.filter((p) => p.zone !== "NEUTRAL");
    }
    // Symbol filter
    if (filters.symbolFilter.trim()) {
      const query = filters.symbolFilter.toLowerCase().trim();
      filtered = filtered.filter((p) => p.symbol.toLowerCase().includes(query));
    }
    return filtered;
  }, [points, filters.minScore, filters.showNeutral, filters.symbolFilter]);

  // Load data on mount
  useEffect(() => {
    if (points.length === 0) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate zone stats from filtered points
  const zoneStats: ZoneStat[] = [
    {
      zone: "GOLDEN_LONG",
      count: filteredPoints.filter((p) => p.zone === "GOLDEN_LONG").length,
      icon: getZoneIcon("GOLDEN_LONG"),
    },
    {
      zone: "GOLDEN_SHORT",
      count: filteredPoints.filter((p) => p.zone === "GOLDEN_SHORT").length,
      icon: getZoneIcon("GOLDEN_SHORT"),
    },
    {
      zone: "WATCH_LONG",
      count: filteredPoints.filter((p) => p.zone === "WATCH_LONG").length,
      icon: getZoneIcon("WATCH_LONG"),
    },
    {
      zone: "WATCH_SHORT",
      count: filteredPoints.filter((p) => p.zone === "WATCH_SHORT").length,
      icon: getZoneIcon("WATCH_SHORT"),
    },
  ];

  const totalSignals = filteredPoints.length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-4 flex flex-wrap items-center gap-4">
        {/* Zone indicators */}
        <div className="flex flex-wrap gap-2">
          {zoneStats.map(({ zone, count, icon }) => (
            <div
              key={zone}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 transition-colors"
            >
              <span style={{ color: getZoneColor(zone) }}>{icon}</span>
              <span className="text-sm font-medium text-gray-700">
                {t(getZoneLabelKey(zone))}
              </span>
              <span
                className="text-sm font-bold px-2 py-0.5 rounded-md text-white min-w-[28px] text-center"
                style={{ backgroundColor: getZoneColor(zone) }}
              >
                {count}
              </span>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="hidden sm:block w-px h-8 bg-gray-200" />

        {/* Filter controls */}
        <div className="flex items-center gap-2">
          {/* Min score filter */}
          <select
            value={filters.minScore}
            onChange={(e) => setFilter("minScore", Number(e.target.value))}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700
                       focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400
                       hover:border-gray-300 transition-colors cursor-pointer"
          >
            <option value={0}>{t("legend.allScores")}</option>
            <option value={50}>{t("legend.scoreMin", { value: 50 })}</option>
            <option value={70}>{t("legend.scoreMin", { value: 70 })}</option>
            <option value={80}>{t("legend.scoreMin", { value: 80 })}</option>
          </select>

          {/* Show neutral toggle */}
          <button
            onClick={() => setFilter("showNeutral", !filters.showNeutral)}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all",
              filters.showNeutral
                ? "bg-blue-50 border-blue-200 text-blue-700"
                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50",
            )}
          >
            {filters.showNeutral ? (
              <Eye className="w-4 h-4" />
            ) : (
              <EyeOff className="w-4 h-4" />
            )}
            {t("legend.showNeutral")}
          </button>

          {/* Symbol search with dropdown */}
          <div className="relative" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder={t("legend.searchPlaceholder")}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setShowDropdown(true);
                setHighlightedIndex(-1);
              }}
              onFocus={() => setShowDropdown(true)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setHighlightedIndex((prev) =>
                    prev < matchingSymbols.length - 1 ? prev + 1 : prev,
                  );
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                } else if (e.key === "Enter") {
                  if (
                    highlightedIndex >= 0 &&
                    matchingSymbols[highlightedIndex]
                  ) {
                    const symbol = matchingSymbols[highlightedIndex];
                    setInputValue(symbol);
                    setFilter("symbolFilter", symbol);
                    setShowDropdown(false);
                    setHighlightedIndex(-1);
                  } else {
                    setFilter("symbolFilter", inputValue);
                    setShowDropdown(false);
                  }
                } else if (e.key === "Escape") {
                  setShowDropdown(false);
                  setHighlightedIndex(-1);
                }
              }}
              className="pl-9 pr-8 py-2 rounded-lg border border-gray-200 bg-white text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400
                         hover:border-gray-300 transition-colors w-40"
            />
            {inputValue && (
              <button
                onClick={() => {
                  setInputValue("");
                  setFilter("symbolFilter", "");
                  setShowDropdown(false);
                  setHighlightedIndex(-1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}

            {/* Dropdown rendered in portal */}
            {showDropdown &&
              matchingSymbols.length > 0 &&
              createPortal(
                <div
                  className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] max-h-60 overflow-auto"
                  style={{
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                  }}
                >
                  {matchingSymbols.map((symbol, index) => (
                    <button
                      key={symbol}
                      onClick={() => {
                        setInputValue(symbol);
                        setFilter("symbolFilter", symbol);
                        setShowDropdown(false);
                        setHighlightedIndex(-1);
                      }}
                      className={cn(
                        "w-full px-3 py-2 text-left text-sm flex items-center justify-between transition-colors",
                        index === highlightedIndex
                          ? "bg-blue-100 text-blue-800"
                          : "hover:bg-blue-50",
                      )}
                    >
                      <span className="font-medium text-gray-800">
                        {symbol}
                      </span>
                      <span className="text-xs text-gray-400">
                        {t("legend.signals", {
                          count: points.filter((p) => p.symbol === symbol)
                            .length,
                        })}
                      </span>
                    </button>
                  ))}
                </div>,
                document.body,
              )}
          </div>
        </div>

        {/* Stats & Refresh */}
        <div className="flex items-center gap-3 ml-auto">
          <div className="text-sm text-gray-500">
            {t("legend.totalSignals", { count: totalSignals })}
          </div>

          <button
            onClick={refreshData}
            disabled={loading}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all",
              "bg-gradient-to-r from-blue-600 to-blue-500 text-white",
              "hover:from-blue-700 hover:to-blue-600",
              "shadow-sm hover:shadow",
              loading && "opacity-50 cursor-not-allowed",
            )}
          >
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            {loading ? t("common.refreshing") : t("common.refresh")}
          </button>
        </div>
      </div>

      {/* Last update time */}
      {lastUpdate && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
          {t("legend.lastUpdate", { time: lastUpdate.toLocaleTimeString() })}
        </div>
      )}
    </div>
  );
}
